import { ASComponentSize } from '@eleventheye/asui';
import { Action, ThunkAction } from '@reduxjs/toolkit';
import { StoreDispatchType, StoreGetStateType } from 'app/store';
import { GameWeek, IRankingCategories } from 'types';

export type StringOrNumber = string | number;

export type AppDispatch = StoreDispatchType;
// export type AppStore = ReturnType<typeof setupStore>;
export type RootState = ReturnType<StoreGetStateType>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;

// Generic types for the app
export type Maybe<T> = T | undefined;

export type ShowBy = 'weekTotals' | 'seasonTotals';

export type InitRankingFn = (category: IRankingCategories) => void;

export enum Platform {
  WebBoth = 'MW/DW',
  MobileWeb = 'MW',
  DesktopWeb = 'DW',
}

export enum TierLevel {
  Plus = 8,
  Premium = 1,
  Prestige = 10,
}

export enum CtaKind {
  SUB = 'SUB',
  ALC = 'ALC',
}

export enum CtaFilterT {
  Alc = 'ALC',
  Sub = 'SUB',
  Plus = 'PLUS',
  Premium = 'PREMIUM',
  Prestige = 'PRESTIGE',
  Mobile = 'MW',
  Desktop = 'DW',
  WebBoth = 'MW/DW',
}

export enum BooleanOperator {
  AND = 'AND',
  OR = 'OR',
  NOT = 'NOT',
}

export enum SearchGroupType {
  Platform = 'platform',
  Tier = 'tier',
  Package = 'package',
}

export type SearchParam = {
  filterGroup: CtaFilterT[];
  boolOperator: BooleanOperator;
  type: SearchGroupType;
};

export type SearchQuery = SearchParam[];

export type CtaId = string;
export type CtaItem = {
  key: CtaId;
  code: string;
  kind: CtaKind;
  startingTierLevel: number;
  platform: Platform;
  screens: CtaScreen[];
};

export type CtaItems = CtaItem[];
export type OneOrManyCtas = CtaItem | CtaItems;

export type CtaItemsWriteToFile = {
  version: string;
  ctas: CtaItems;
  timestamp: number;
};

export type MembershipTier = { name: string; level: number };
export type MembershipTiers = MembershipTier[];
export type OneOrManyTiers = MembershipTier | MembershipTiers;

export type CtaToTier = { cta: CtaId; code: string; tiers: number[] };
export type CtaToTiers = CtaToTier[];

export type CtaScreen = {
  ctaId: CtaId;
  ctaCode: string;
  platform: Platform;
  screen: ScreenName;
  target: ScreenTarget;
  description: string;
  screenPosition: ScreenPosition;
};

export enum ScreenTarget {
  Button = 'Button',
  Icon = 'Icon',
  Link = 'Link',
  Text = 'Text',
  Panel = 'Panel',
  Card = 'Card',
  Banner = 'Banner',
  Carousel = 'Carousel',
  Nav = 'Nav',
  Snackbar = 'Snackbar',
  Notification = 'Notification',
  Popup = 'Popup',
  Paywall = 'Paywall',
  NoScreen = 'NoScreen',
}

export enum ScreenName {
  Unknown = 'Unknown',
  CommonForAllScreens = 'CommonForAllScreens',
  ActivityCenter = 'ActivityCenter',
  MyPowerUps = 'MyPowerUps',
  Photos = 'Photos',
  Account = 'Account',
  Profile = 'Profile',
  City = 'City',
  Inbox = 'Inbox',
  Search = 'Search',
  Recommended = 'Recommended',
  NewMembers = 'NewMembers',
  Online = 'Online',
  Interests = 'Interests',
  MeetMe = 'MeetMe',
  Likes = 'Likes',
  ProfileBoost = 'ProfileBoost',
  PhotosBoost = 'PhotosBoost',
  MeetMeBoost = 'MeetMeBoost',
  InterestedInMeBoost = 'InterestedInMeBoost',
  MessageInboxBoost = 'MessageInboxBoost',
  OnlineBoost = 'OnlineBoost',
  SearchBoost = 'SearchBoost',
  RecommendedBoost = 'RecommendedBoost',
  NewMembersBoost = 'NewMembersBoost',
  CityBoost = 'CityBoost',
  ProfileHighlighter = 'ProfileHighlighter',
}

export enum ScreenPosition {
  Top = 'top',
  TopLeft = 'top-left',
  TopRight = 'top-right',
  Center = 'center',
  CenterLeft = 'center-left',
  CenterRight = 'center-right',
  Bottom = 'bottom',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
}

export enum ModalAction {
  Update = 'update',
  Cancel = 'cancel',
  Add = 'add',
}

export type EditStartingTierAction = {
  action: Omit<ModalAction, ModalAction.Add>;
  ctaItem: CtaItem;
};

export type AddEditScreenLocationAction = {
  action: ModalAction;
  ctaItem: CtaItem;
};

// FIRST_CONTACTS_LIMIT_CTA
// {platform: "MW", screen: 'My Power-ups', target: "button", description: "First contact, get more", screenPosition: 'center'},
// {platform: "DW", screen: 'My Power-ups', target: "button", description: "First contact, get more", screenPosition: 'center-left'},

export enum Direction {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
}

type ASPaddingSizeTuple = [ASComponentSize, ASComponentSize];
type ASPaddingSizeTriple = [ASComponentSize, ASComponentSize, ASComponentSize];
type ASPaddingSizeQuad = [ASComponentSize, ASComponentSize, ASComponentSize, ASComponentSize];
export type ASPaddingSize = ASComponentSize | ASPaddingSizeTuple | ASPaddingSizeTriple | ASPaddingSizeQuad;

/* cspell: disable */
export type ASPaddingTopSize = 'ASPDTASSmall' | 'ASPDTASMedium' | 'ASPDTASLarge';
export type ASPaddingRightSize = 'ASPDRASSmall' | 'ASPDRASMedium' | 'ASPDRASLarge';
export type ASPaddingBottomSize = 'ASPDBASSmall' | 'ASPDBASMedium' | 'ASPDBASLarge';
export type ASPaddingLeftSize = 'ASPDLASSmall' | 'ASPDLASMedium' | 'ASPDLASLarge';

export type ASPaddingKeys = ASPaddingTopSize & ASPaddingRightSize & ASPaddingBottomSize & ASPaddingLeftSize;

export type PaddingSizeToClsType = {
  [key in ASPaddingKeys]: string;
};

export interface AppUser {
  uid: string;
  email: string;
  name?: string;
  isAnonymous: boolean;
  emailVerified: boolean;
  isEditor: boolean;
  createdOn: number;
  modifiedOn: number;
}

export enum AuthMode {
  Login = 'Login',
  Register = 'Register',
  PasswordReset = 'PasswordReset',
}

export enum ScreenSize {
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
  ExtraLarge = 'ExtraLarge',
  ExtraExtraLarge = 'ExtraExtraLarge',
}

export enum GameDetailsHeadingSection {
  Season,
  Week,
  Game,
}
export type ActiveStageState =
  | 'NONE'
  | 'SCHEDULE'
  | 'GAME_RESULTS'
  | 'TEAM_RESULTS'
  | 'TEAMS_ON_BYE'
  | 'RANKINGS'
  | 'SEASON_STANDINGS'
  | 'TEAM_DETAILS'
  | 'TEAM_STANDINGS';

export type StageState = {
  gameScheduleLoaded: boolean;
  gameResultsLoaded: boolean;
  teamResultsLoaded: boolean;
  byeWeekTeamsLoaded: boolean;
  rankingsLoaded: boolean;
  teamDetailsLoaded: boolean;
  teamStandingsLoaded: boolean;
};

export type WeeklyResultT = { [key in string]: string };
export type SeasonResultT = WeeklyResultT[];
export type FailureT = {
  weekID: GameWeek;
  stage: ActiveStageState;
  expected?: number;
  actual?: number;
};

export type AppMonitorState = {
  appActivated: boolean;
  loadingStage: ActiveStageState;
  latestAvailableWeek: GameWeek;
  stages: StageState;
};
